import { useState, useEffect, useRef, useLayoutEffect, useReducer, useCallback, useMemo } from 'react';
import type { Dispatch, Reducer } from 'react';
import _debounce from 'lodash/debounce';
import _uniq from 'lodash/uniq';

import { getWindowWidth } from 'utils/window';

export const useConstant = <T>(fn: () => T): T => {
  const ref = useRef<{ v: T } | null>(null);
  if (!ref.current) {
    ref.current = { v: fn() };
  }
  return ref.current.v;
};

export const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T | undefined>(undefined);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

type WindowSize = {
  width: number;
  height: number;
};

const windowSizeEvents: Set<() => void> = new Set();
const onResize = () => windowSizeEvents.forEach((fn) => fn());

export const useDebouncedWindowSize = (debounceTime = 100): WindowSize => {
  const [size, setSize] = useState<WindowSize>(
    useConstant(() => ({
      width: getWindowWidth(),
      height: window.innerHeight,
    })),
  );

  const handler = _debounce(() => {
    setSize({
      width: getWindowWidth(),
      height: window.innerHeight,
    });
  }, debounceTime);

  useEffect(() => {
    if (windowSizeEvents.size === 0) {
      window.addEventListener('resize', onResize);
    }
    windowSizeEvents.add(handler);

    return () => {
      windowSizeEvents.delete(handler);
      if (windowSizeEvents.size === 0) {
        window.removeEventListener('resize', onResize);
      }
    };
    // we intentionally skip dependencies – `handler` is memoised via lodash
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return size;
};

export function useInterval(callback: () => void, delay: number | null): void {
  const savedCallback = useRef<(() => void) | null>(null);

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current?.();
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

// Debounce a changing value, returning the debounced value only after the specified delay.
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function useEffectOnMount(callback: () => void | (() => void)): void {
  const savedCallback = useRef<(() => void | (() => void)) | null>(null);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    const cleanup = savedCallback.current?.();
    if (typeof cleanup === 'function') {
      return cleanup;
    }
  }, []);
}

export const useUpdateReason = (name: string, props: Record<string, unknown>): void => {
  const previousProps = useRef<Record<string, unknown> | undefined>(undefined);

  useEffect(() => {
    if (previousProps.current) {
      const keysTable: Record<string, true> = {} as Record<string, true>;

      for (const key in previousProps.current) {
        keysTable[key] = true;
      }
      for (const key in props) {
        keysTable[key] = true;
      }

      const allKeys = Object.keys(keysTable);
      const changesTable: Record<string, { from: unknown; to: unknown }> = {};

      for (const key of allKeys) {
        if (previousProps.current[key] !== props[key]) {
          changesTable[key] = {
            from: previousProps.current[key],
            to: props[key],
          };
        }
      }

      if (Object.keys(changesTable).length) {
        // eslint-disable-next-line no-console
        console.log('%c [Update Reason]', 'font-weight: bold', name, changesTable);
        // eslint-disable-next-line no-console
        console.count(name);
      }
    }

    previousProps.current = props;
  });
};

export const useToggle = (initialState = false) => {
  const [isOn, _setIsOn] = useState<boolean>(initialState);

  return useMemo(
    () => ({
      isOn,
      set: (s: boolean) => _setIsOn(s),
      toggle: () => _setIsOn((s) => !s),
      setOn: () => _setIsOn(true),
      setOff: () => _setIsOn(false),
    }),
    [isOn],
  );
};

// https://usehooks.com/useOnClickOutside/
export const useOnClickOutside = <T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  handler: (event: MouseEvent | TouchEvent) => void,
): void => {
  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      if (!ref.current || ref.current.contains(event.target as Node)) {
        return;
      }

      handler(event);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
};

export function useLockBodyScroll(active = true): void {
  useLayoutEffect(() => {
    if (active) {
      const tmp = document.body.dataset.lockStack;
      const newVal = tmp ? +tmp + 1 : 1;
      document.body.dataset.lockStack = String(newVal);

      if (newVal > 0) {
        document.body.classList.add('scroll-lock');
      }
    }

    return () => {
      if (!active) {
        return;
      }

      const tmp = document.body.dataset.lockStack;
      const newVal = tmp ? +tmp - 1 : 0;
      document.body.dataset.lockStack = String(newVal);

      if (newVal < 1) {
        document.body.classList.remove('scroll-lock');
      }
    };
  }, [active]);
}

export function useBlockHorizontalOverscroll(active = true): void {
  useLayoutEffect(() => {
    if (active) {
      document.body.classList.add('block-horizontal-overscroll');
    }

    return () => {
      document.body.classList.remove('block-horizontal-overscroll');
    };
  }, [active]);
}

type ErrorMap<T> = Partial<Record<keyof T, string>>;

type FormAction<T extends Record<string, unknown>> =
  | { type: 'RESET'; payload?: T }
  | { type: 'CHANGE'; key: keyof T; value: T[keyof T] };

const formReducer = <T extends Record<string, unknown>>(state: T, action: FormAction<T>): T => {
  switch (action.type) {
    case 'RESET':
      return action.payload ?? ({} as T);
    case 'CHANGE':
      return { ...state, [action.key]: action.value } as T;
    default:
      return state;
  }
};

type TouchedState<T> = Partial<Record<keyof T, true>>;

type TouchedAction<K> = { type: 'RESET' } | { key: K };

const touchedReducer = <T extends Record<string, unknown>>(
  state: TouchedState<T>,
  action: TouchedAction<keyof T>,
): TouchedState<T> => {
  if ('type' in action && action.type === 'RESET') {
    return {};
  }
  if ('key' in action) {
    return { ...state, [action.key]: true } as TouchedState<T>;
  }
  return state;
};

// Nope validators can also return a string (object-level rule failure) or
// undefined (no shape). Top-level form schemas always use `.shape(...)`, so
// those cases never occur here; `toErrorMap` narrows them away.
type ValidationOutcome<T> = ErrorMap<T> | string | undefined;

type ValidationSchema<T> = {
  validate(values: T): ValidationOutcome<T>;
};

const toErrorMap = <T>(result: ValidationOutcome<T>): ErrorMap<T> =>
  typeof result === 'object' && result !== null ? result : {};

type UseFormArgs<T extends Record<string, unknown>> = {
  initialValues?: T;
  validationSchema?: ValidationSchema<T>;
  validate?: (values: T) => ValidationOutcome<T>;
  onSubmit: (values: T, helpers: { setIsSubmitting: (v: boolean) => void; data: unknown }) => void;
};

type InputChangeEvent = React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>;

export const useForm = <T extends Record<string, unknown>>({
  initialValues = {} as T,
  validationSchema,
  validate,
  onSubmit,
}: UseFormArgs<T>) => {
  const [state, dispatch] = useReducer(formReducer<T>, initialValues);
  const [touched, dispatchTouched] = useReducer(touchedReducer<T>, {} as TouchedState<T>);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<ErrorMap<T>>({});

  const validateForm = useCallback((): ErrorMap<T> => {
    if (validate) {
      return toErrorMap(validate(state));
    }
    if (validationSchema) {
      return toErrorMap(validationSchema.validate(state));
    }
    return {};
  }, [state, validate, validationSchema]);

  useEffect(() => {
    if (!submitted || !(validationSchema || validate)) {
      return;
    }
    setErrors(validateForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted, validateForm, validationSchema]);

  const isInputEvent = (value: unknown): value is InputChangeEvent =>
    typeof value === 'object' && value !== null && 'target' in value && 'nativeEvent' in value;

  const handleChange = useCallback((eventOrValue: unknown, name?: keyof T) => {
    let key: keyof T;
    let value: T[keyof T];

    if (isInputEvent(eventOrValue)) {
      key = eventOrValue.target.name as keyof T;
      value = eventOrValue.target.value as unknown as T[keyof T];
    } else {
      if (name === undefined) {
        throw new TypeError('Invalid handleChange usage: name is undefined');
      }
      key = name;
      value = eventOrValue as T[keyof T];
    }

    dispatchTouched({ key });
    dispatch({ type: 'CHANGE', key, value });
  }, []);

  const submitForm = useCallback(
    (data?: unknown) => {
      setSubmitted(true);
      const validationErrors = validateForm();

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      onSubmit(state, { setIsSubmitting, data });
    },
    [validateForm, onSubmit, state],
  );

  const validateField = (value: T[keyof T], field: keyof T) => {
    if (!validationSchema) return;

    const error = toErrorMap(validationSchema.validate({ [field]: value } as T));

    if (!value) {
      setErrors((prev) => ({ ...prev, ...error }));
      return;
    }

    setErrors((prev) => {
      const { [field]: _removed, ...rest } = prev;
      return rest as ErrorMap<T>;
    });
  };

  const resetForm = (defaultValues: Partial<T> = {}) => {
    const resetValues = { ...initialValues, ...defaultValues } as T;
    dispatch({ type: 'RESET', payload: resetValues });
    dispatchTouched({ type: 'RESET' });
    setErrors({});
    setSubmitted(false);
    setIsSubmitting(false);
  };

  return {
    handleChange,
    values: state,
    touched,
    errors,
    submitted,
    handleSubmit: submitForm,
    isSubmitting,
    validateField,
    resetForm,
  } as const;
};

type ToggleEntry<K extends PropertyKey = string, V = unknown> = {
  propKey: K;
  value: V;
};

export const useToggleableCollection = <K extends PropertyKey = string, V = unknown>(
  initialState: ToggleEntry<K, V>[] = [],
) => {
  const [state, setState] = useState<ToggleEntry<K, V>[]>(initialState);

  const toggleItem = ({ propKey, value }: ToggleEntry<K, V>): void => {
    const filtered = state.filter((x) => x.propKey !== propKey || x.value !== value);
    const next = filtered.length === state.length ? [...filtered, { propKey, value }] : filtered;
    setState(next);
  };

  return [state, toggleItem] as const;
};

export function useIsMounted(): () => boolean {
  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  return useCallback(() => isMounted.current, []);
}

export function useSafeReducer<R, A>(reducer: Reducer<R, A>, initialArg: R): [R, Dispatch<A>] {
  const [state, internalDispatch] = useReducer(reducer, initialArg);

  const isMounted = useIsMounted();

  const dispatch: Dispatch<A> = useCallback(
    (action) => {
      if (isMounted()) {
        internalDispatch(action);
      }
    },
    [internalDispatch, isMounted],
  );

  return [state, dispatch];
}

export const useMultiselect = <T extends number>(items: T[]) => {
  const [selected, setSelected] = useState<T[]>([]);

  const selectAll = useCallback(() => {
    setSelected([...items]);
  }, [items]);

  const deselectAll = useCallback(() => {
    setSelected([]);
  }, []);

  const selectItems = useCallback((itemIds: T[]) => {
    setSelected((prev) => _uniq(prev.concat(itemIds)));
  }, []);

  const deselectItems = useCallback((itemIds: T[]) => {
    setSelected((prev) => prev.filter((id) => !itemIds.includes(id)));
  }, []);

  const isItemSelected = useCallback(
    (itemId: T) => {
      return selected.includes(itemId);
    },
    [selected],
  );

  const toggleItem = useCallback(
    (itemId: T) => {
      if (isItemSelected(itemId)) {
        setSelected((prev) => prev.filter((id) => id !== itemId));
      } else {
        setSelected((prev) => prev.concat(itemId));
      }
    },
    [isItemSelected],
  );

  const value = useMemo(
    () => ({
      totalItems: items.length || 0,
      selected,
      selectedCount: selected.length,
      areAllItemsSelected: selected.length === (items?.length || 0),
      selectItems,
      deselectItems,
      selectAll,
      deselectAll,
      isItemSelected,
      toggleItem,
    }),
    [items.length, selected, selectItems, deselectItems, selectAll, deselectAll, isItemSelected, toggleItem],
  );

  return value;
};

export const useKeyboardShortcut = (
  callback: () => void,
  {
    key,
    shiftKey = false,
    metaKey = false,
    ctrlKey = false,
  }: {
    key: string;
    shiftKey?: boolean;
    metaKey?: boolean;
    ctrlKey?: boolean;
  },
): void => {
  useEffect(() => {
    const handleKeyPress = (e?: KeyboardEvent) => {
      if (
        e?.key?.toLowerCase?.() === key.toLowerCase() &&
        e.shiftKey === shiftKey &&
        e.metaKey === metaKey &&
        e.ctrlKey === ctrlKey
      ) {
        callback();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [callback, key, shiftKey, metaKey, ctrlKey]);
};

export const useResizeObserver = (): [React.RefObject<HTMLDivElement | null>, { width: number; height: number }] => {
  const ref = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  useEffect(() => {
    const observeTarget = ref.current;
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      });
    });

    if (observeTarget) {
      resizeObserver.observe(observeTarget);
    }

    return () => {
      if (observeTarget) {
        resizeObserver.unobserve(observeTarget);
      }
    };
  }, [ref]);

  return [ref, dimensions];
};
